import { isEmpty } from "@swiggy-private/js-utils";
import { BR_TAG, SINGLE_SPACE_IDENTIFIER, VARIANTS } from "../constants";
import { PLACEHOLDERS } from "@super-ui/constants";

import { extractKeyFromWord } from "@super-ui/components/m2c-swiggy-one-halfcard/helpers";

import { TGCTAs } from "@swiggy-private/cart-types";
import { emojiToTextConverter } from "@super-ui/components/swiggy-one-widget-dls-wrapper/helpers/bottomsheetNodeBuilder";
import { CTATypes } from "@super-ui/components/swiggy-one-widget-dls-wrapper";

export * from "./sdkResponse";
export * from "./genericHelpers";
export * from "./ctaHelpers";

/**
 * Replaces text with emoji if the word contains emoji placeholders.
 *
 * @param word - The word to be processed.
 * @returns The word with emoji replaced or an empty string if the word is empty.
 */
export const replaceTextWithEmoji = (word: string) => {
    if (isEmpty(word)) return "";

    if (word.includes(PLACEHOLDERS.EMOJI_OPEN) && word.includes(PLACEHOLDERS.EMOJI_CLOSE)) {
        const emoji = word.replace(PLACEHOLDERS.EMOJI_OPEN, "").replace(PLACEHOLDERS.EMOJI_CLOSE, "");
        return emojiToTextConverter(emoji);
    }

    return word;
};

/**
 * Parses the input text to extract a variant and replace words with emojis.
 * If a placeholder is found, it updates the variant accordingly.
 *
 * @param text - The input text to be parsed.
 * @returns An object containing the detected variant and the updated text.
 */
export const textParser = (text: string) => {
    // Default to variant V1 if no placeholders are found
    let variant = VARIANTS.DEFAULT;

    // Return early if the text is empty
    if (isEmpty(text)) return { variant, text: "" };

    // Process the text by splitting it into words, replacing with emojis, and checking for placeholders
    const updatedText = text
        .split(" ") // Split the text into words
        .map((word: string) => {
            // Replace the word with an emoji, if applicable
            const updatedWord = replaceTextWithEmoji(word);

            // Check if the word contains placeholders for variant extraction
            if (updatedWord.includes(PLACEHOLDERS.OPEN) && updatedWord.includes(PLACEHOLDERS.CLOSE)) {
                // Extract the key from the word between the placeholders
                const key = extractKeyFromWord(updatedWord, {
                    OPEN: PLACEHOLDERS.OPEN,
                    CLOSE: PLACEHOLDERS.CLOSE,
                }).toUpperCase();

                // Update the variant based on the extracted key
                variant = VARIANTS[key as keyof typeof VARIANTS];
                return ""; // Remove the word since it's a placeholder
            }

            // Return the updated word (with emoji if replaced)
            return updatedWord;
        })
        .filter(Boolean) // Filter out any empty strings
        .join(" "); // Rejoin the words into a single string

    // Return the detected variant and the updated text
    return { variant, text: updatedText };
};

/**
 * Parses the given price string and returns an object with the base price and new price. Text expected: "<s>199</s> <br> 10".
 * If the price is empty, it returns an object with empty base price and new price.
 * If the price contains a line break tag, it splits the price into base price and new price.
 * Otherwise, it sets the new price as the given price.
 *
 * @param price - The price string to be parsed.
 * @returns An object with the base price and new price.
 */
export const priceParser = (price: string) => {
    const updatedPrice = { basePrice: "", newPrice: "" };
    if (isEmpty(price)) {
        return updatedPrice;
    }

    if (price.includes(BR_TAG)) {
        const splittedPrice = price.split(BR_TAG);
        return { basePrice: splittedPrice[0].trim(), newPrice: splittedPrice[1].trim() };
    }

    updatedPrice.newPrice = price;
    return updatedPrice;
};

/**
 * Validates an array of CTAs (Call to Action) objects.
 *
 * @param ctas - The array of CTAs to validate.
 * @returns A boolean indicating whether the CTAs are valid or not.
 */
export const validateCtas = (ctas: TGCTAs[]) => {
    if (isEmpty(ctas)) {
        return false;
    }

    const invalidCta = ctas.find((cta: TGCTAs) => isEmpty(cta?.text) || isEmpty(cta?.type));
    return isEmpty(invalidCta);
};

/**
 * Splits the given text into two sections, where the second section contains the last word.
 * Expected text: "View All benefits >" if the chevron goes in next line the benefits should go with it.
 * If the text is empty, both sections will be empty strings.
 *
 * @param text - The text to be split.
 * @returns An object with two sections: `firstSection` and `secondSection`.
 */
export const getlastWordSeparated = (text: string) => {
    const texts = { firstSection: "", secondSection: "" };
    if (isEmpty(text)) {
        return texts;
    }

    const splittedText = text.split(SINGLE_SPACE_IDENTIFIER);
    const secondSection = `${SINGLE_SPACE_IDENTIFIER}${splittedText.pop()}`;
    const firstSection = splittedText.join(SINGLE_SPACE_IDENTIFIER);

    return { firstSection, secondSection };
};

/**
 * Checks if the given type matches the specified CTA type.
 *
 * @param type - The type to check.
 * @param ctaType - The CTA type to compare against. Defaults to `CTATypes.ONE_TAP_ADD`.
 * @returns `true` if the type matches the CTA type, otherwise `false`.
 */
export const checkCtaType = (type?: string, ctaType = CTATypes.ONE_TAP_ADD) => {
    return type?.toUpperCase() === ctaType;
};
